import { ChangePulseMarker, ProposedValue } from '@/features/ai-changeset/components/ProposedValue';
import type { NavigationStore } from '@/features/ai-changeset/hooks/useChangeNavigation';
import { changedRowClass, type RunPosition } from '@/features/ai-changeset/utils/changeset-view';
import { DraftTag } from '@/features/menu/components/DraftTag';
import type { PromotionDraftRow } from '@/features/promotions/utils/promotion-draft';
import {
  conflictText,
  promotionSummary,
  promotionWhen,
} from '@/features/promotions/utils/promotion-summary';
import type { useT } from '@/i18n';
import { Badge, Chip, ListItem } from 'framework7-react';

export interface PromotionRowContext {
  currency: string;
  timezone: string;
  locale: string;
  now: number;
  names: Map<string, string>;
}

function isLive(row: PromotionDraftRow): boolean {
  if (row.status === 'archived') return false;
  if (row.status === 'new' || row.status === 'restored') return true;
  return row.promotion?.isActive ?? false;
}

function dateBadge(row: PromotionDraftRow, now: number, tp: ReturnType<typeof useT>['promotions']) {
  if (!isLive(row)) return null; // archived gets its own badge
  const { startsAt, endsAt } = row.value;
  if (startsAt && Date.parse(startsAt) > now) return { label: tp.scheduledBadge, color: 'orange' };
  if (endsAt && Date.parse(endsAt) < now) return { label: tp.expiredBadge, color: 'gray' };
  return null;
}

export function PromotionRow({
  row,
  run,
  store,
  t,
  context,
  onArchive,
}: {
  row: PromotionDraftRow;
  run: RunPosition | null;
  store: NavigationStore;
  t: ReturnType<typeof useT>;
  context: PromotionRowContext;
  onArchive: (() => void) | null;
}) {
  const tp = t.promotions;
  const { value, before, status, promotion } = row;
  const archived = status === 'archived';
  const restored = status === 'restored';
  const live = isLive(row);
  const draftClass =
    status === 'new' || restored ? 'ai-changed-text' : archived ? 'quick-edit-old' : '';
  const name = value.name.en ?? '';
  const beforeName = before?.name.en ?? name;
  const summary = promotionSummary(value, context.currency, tp);
  const beforeSummary = before ? promotionSummary(before, context.currency, tp) : summary;
  const drafted = status === 'new' || status === 'updated';
  const when = (draftValue: typeof value) =>
    promotionWhen(draftValue, context.timezone, context.locale, tp);
  const text = drafted ? when(value) : value.code ? tp.codeDisplay(value.code.code) : '';
  const beforeText = before ? when(before) : text;
  const badge = dateBadge(row, context.now, tp);
  const linked = promotion !== null && status === null;
  const classes = [
    'menu-item-row',
    archived && 'menu-item-row--muted menu-item-row--removed',
    changedRowClass(run),
  ].filter(Boolean);

  return (
    <ListItem
      key={row.id}
      link={linked ? `/promotions/${row.id}` : undefined}
      swipeout={onArchive !== null}
      className={classes.join(' ')}
      data-quick-edit-target={row.id}
      data-view-row={row.id}
    >
      {status !== null && (
        <ChangePulseMarker
          slot="root-start"
          store={store}
          id={row.id}
          testId={`promotion-pulse-${row.id}`}
        />
      )}
      <span slot="title" className={draftClass}>
        {beforeName !== name ? <ProposedValue start before={beforeName} after={name} /> : name}
        {status === 'new' && <DraftTag status="new" testId={`promotion-tag-${row.id}`} />}
        {archived && (
          <Chip
            text={tp.archivedBadge}
            outline
            className="quick-edit-tag quick-edit-tag--removed"
            data-testid={`promotion-tag-${row.id}`}
          />
        )}
        {restored && (
          <Chip
            text={t.promotionsQuickEdit.restoredTag}
            outline
            className="quick-edit-tag"
            data-testid={`promotion-tag-${row.id}`}
          />
        )}
      </span>
      <span slot="subtitle" className={draftClass}>
        {beforeSummary !== summary ? (
          <ProposedValue start before={beforeSummary} after={summary} />
        ) : (
          summary
        )}
      </span>
      {(text || beforeText) && (
        <span slot="text" className={draftClass}>
          {beforeText !== text ? <ProposedValue start before={beforeText} after={text} /> : text}
        </span>
      )}
      {row.conflicts.length > 0 && (
        <div
          slot="footer"
          className="quick-edit-detail-lines"
          data-testid={`promotion-conflicts-${row.id}`}
        >
          {row.conflicts.map((conflict) => (
            <div
              key={`${conflict.otherPromotionId}-${conflict.effect}`}
              className="quick-edit-line"
            >
              {conflictText(
                conflict,
                context.names.get(conflict.otherPromotionId) ?? '',
                context,
                tp,
                t.promotionsQuickEdit,
              )}
            </div>
          ))}
        </div>
      )}
      <div slot="after" style={{ display: 'flex', gap: 4 }}>
        {promotion && !promotion.isActive && !restored && (
          <Badge color="gray">{tp.archivedBadge}</Badge>
        )}
        {badge && <Badge color={badge.color}>{badge.label}</Badge>}
        {live && value.trigger === 'CODE_REQUIRED' && <Badge color="blue">{tp.codeBadge}</Badge>}
      </div>
      {onArchive && (
        <div slot="swipeout-actions-right">
          <button type="button" className="swipeout-close color-red" onClick={onArchive}>
            {tp.archive}
          </button>
        </div>
      )}
    </ListItem>
  );
}
